import type { Firestore } from 'firebase-admin/firestore'
import type { RedisClient } from '../../core/redis/client.js'
import { EventEmitter } from 'node:events'
import type {
  Quest,
  QuestType,
  QuestCategory,
  QuestReward,
  QuestProgress
} from '@my-roadtrip/types'
import { NotFoundError, ValidationError } from '../../common/errors/index.js'
import { createLogger, trackPerformance } from '../../common/utils/logger.js'
import { createBatchBuilder } from '../../common/utils/firestore-batch.js'
import { removeUndefinedFields, safeJsonParse } from '../../utils/sanitize.js'
import type {
  FirestoreData,
  FirestoreDoc,
  FirestoreDocLike,
  FirestoreTransaction
} from '../../common/types/firestore.js'
import { toDateSafe, toDateOrNow, toDateOrUndefined } from '../../common/utils/normalize.js'

interface QuestTemplate {
  type: QuestType
  category: QuestCategory
  title: string
  description: string
  difficulty: 'easy' | 'medium' | 'hard'
  targetValue: number
  reward: QuestReward
}

/**
 * Quest Service - Handles quest generation, completion tracking, and rewards
 * 
 * Features:
 * - Daily quests (3 per day, 24h expiry)
 * - Weekly quests (1 per week, 7 days expiry)
 * - Quest completion tracking
 * - Reward distribution (points, XP, badges)
 * - Redis caching for active quests
 * - Batch operations for performance
 * - Structured error handling
 */
export class QuestService {
  private readonly events: EventEmitter
  // ✅ Increased TTL: 10 min dev, 4 hours prod (quests rarely change)
  private readonly CACHE_TTL = process.env.NODE_ENV === 'production' ? 14400 : 600
  private readonly logger = createLogger('QuestService')

  constructor(
    private readonly firestore: Firestore,
    private readonly redis: RedisClient,
    events?: EventEmitter
  ) {
    // Use provided EventEmitter or create new one
    this.events = events || new EventEmitter()
  }

  private async redisGet(key: string): Promise<string | null> {
    try {
      return await this.redis.get(key)
    } catch (error) {
      this.logger.warn('Redis GET failed, continuing without cache', {
        key,
        error: (error as Error).message
      })
      return null
    }
  }

  private async redisSetex(key: string, ttl: number, value: string): Promise<void> {
    try {
      await this.redis.setex(key, ttl, value)
    } catch (error) {
      this.logger.warn('Redis SETEX failed, cache write skipped', {
        key,
        error: (error as Error).message
      })
    }
  }

  private async redisDel(...keys: string[]): Promise<void> {
    if (!keys.length) return
    try {
      await this.redis.del(...keys)
    } catch (error) {
      this.logger.warn('Redis DEL failed, cache invalidation skipped', {
        keys,
        error: (error as Error).message
      })
    }
  }

  // ========== QUEST GENERATION ==========

  /**
   * Generate daily quests for a user (3 quests, 24h expiry)
   * 
   * @throws {ValidationError} If userId is invalid
   */
  async generateDailyQuests(userId: string): Promise<Quest[]> {
    if (!userId || typeof userId !== 'string') {
      throw new ValidationError('Invalid userId', { userId })
    }

    return await trackPerformance(
      'generateDailyQuests',
      async () => {
        // Check if user already has active daily quests
        const existing = await this.getActiveQuests(userId, 'daily')
        if (existing.length >= 3) {
          this.logger.debug('User already has 3 active daily quests', { userId, count: existing.length })
          return existing
        }

        const templates = this.getDailyQuestTemplates()
        const selected = this.selectRandomTemplates(templates, 3)

        const quests: Quest[] = selected.map(template => ({
          id: '', // Will be set by Firestore
          userId,
          type: template.type,
          category: 'daily',
          title: template.title,
          description: template.description,
          difficulty: template.difficulty,
          targetValue: template.targetValue,
          currentValue: 0,
          progress: { current: 0, target: template.targetValue },
          status: 'active',
          reward: template.reward,
          expiresAt: this.getExpiryDate('daily'),
          createdAt: new Date(),
          updatedAt: new Date()
        }))

        const created = await this.createQuestsBatch(quests)
        this.logger.info('Generated daily quests', { userId, count: created.length })
        return created
      },
      this.logger
    )
  }

  /**
   * Generate weekly quest for a user (1 quest, 7 days expiry)
   * 
   * @throws {ValidationError} If userId is invalid
   */
  async generateWeeklyQuest(userId: string): Promise<Quest | null> {
    if (!userId || typeof userId !== 'string') {
      throw new ValidationError('Invalid userId', { userId })
    }

    return await trackPerformance(
      'generateWeeklyQuest',
      async () => {
        // Check if user already has active weekly quest
        const existing = await this.getActiveQuests(userId, 'weekly')
        if (existing.length > 0) {
          this.logger.debug('User already has active weekly quest', { userId })
          return existing[0]
        }

        const templates = this.getWeeklyQuestTemplates()
        const selected = this.selectRandomTemplates(templates, 1)[0]

        const quest: Quest = {
          id: '', // Will be set by Firestore
          userId,
          type: selected.type,
          category: 'weekly',
          title: selected.title,
          description: selected.description,
          difficulty: selected.difficulty,
          targetValue: selected.targetValue,
          currentValue: 0,
          progress: { current: 0, target: selected.targetValue },
          status: 'active',
          reward: selected.reward,
          expiresAt: this.getExpiryDate('weekly'),
          createdAt: new Date(),
          updatedAt: new Date()
        }

        const created = await this.createQuest(quest)
        this.logger.info('Generated weekly quest', { userId, questId: created.id })
        return created
      },
      this.logger
    )
  }

  // ========== QUEST TRACKING ==========

  /**
   * Update quest progress
   * 
   * @throws {ValidationError} If questId or increment is invalid
   * @throws {NotFoundError} If quest not found or not active
   */
  async updateQuestProgress(
    questId: string,
    increment: number
  ): Promise<Quest | null> {
    if (!questId || typeof questId !== 'string') {
      throw new ValidationError('Invalid questId', { questId })
    }
    if (typeof increment !== 'number' || !Number.isFinite(increment) || increment <= 0) {
      throw new ValidationError('Invalid increment value', { increment })
    }

    return await trackPerformance(
      'updateQuestProgress',
      async () => {
        const quest = await this.getQuestById(questId)
        if (!quest) {
          throw new NotFoundError('Quest not found', { questId })
        }
        if (quest.status !== 'active') {
          this.logger.warn('Cannot update inactive quest', { questId, status: quest.status })
          return null
        }

        const newValue = Math.min(quest.currentValue + increment, quest.targetValue)
        const progress: QuestProgress = {
          current: newValue,
          target: quest.targetValue
        }

        const updatedAt = new Date()
        await this.firestore
          .collection('quests')
          .doc(questId)
          .update({
            currentValue: newValue,
            progress,
            updatedAt
          })

        // Check if quest is completed
        const isCompleted = newValue >= quest.targetValue
        const completionTimestamp = isCompleted ? updatedAt : null
        if (isCompleted) {
          await this.completeQuest(questId, quest, completionTimestamp)
        }

        // Invalidate cache
        if (!isCompleted) {
          await this.redisDel(`quest:${questId}`, `quests:active:${quest.userId}`)
        }

        this.logger.info('Quest progress updated', {
          questId,
          userId: quest.userId,
          newValue,
          targetValue: quest.targetValue,
          completed: isCompleted
        })

        return {
          ...quest,
          currentValue: newValue,
          progress,
          status: isCompleted ? 'completed' : quest.status,
          completedAt: isCompleted ? completionTimestamp ?? new Date() : quest.completedAt
        }
      },
      this.logger
    )
  }

  /**
   * Track objective/quest progress by type for the current user
   * Increments all active quests matching the provided type.
   */
  async trackObjectiveProgress(
    userId: string,
    questType: QuestType,
    amount = 1
  ): Promise<{ updated: Quest[]; completed: Quest[] }> {
    if (!userId || typeof userId !== 'string') {
      throw new ValidationError('Invalid userId', { userId })
    }
    if (!questType) {
      throw new ValidationError('Invalid quest type', { questType })
    }
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
      throw new ValidationError('Invalid amount', { amount })
    }

    const active = await this.getActiveQuests(userId)
    const matching = active.filter(q => q.type === questType)

    if (!matching.length) {
      return { updated: [], completed: [] }
    }

    const updated: Quest[] = []
    const completed: Quest[] = []

    for (const quest of matching) {
      const result = await this.updateQuestProgress(quest.id, amount)
      if (result) {
        updated.push(result)
        if (result.status === 'completed') {
          completed.push(result)
        }
      }
    }

    return { updated, completed }
  }

  /**
   * Complete a quest and award rewards
   * 
   * @throws {ValidationError} If questId is invalid
   * @throws {NotFoundError} If quest not found or not active
   */
  async completeQuest(
    questId: string,
    quest?: Quest,
    completionTimestamp: Date | null = null
  ): Promise<boolean> {
    if (!questId || typeof questId !== 'string') {
      throw new ValidationError('Invalid questId', { questId })
    }

    return await trackPerformance(
      'completeQuest',
      async () => {
        const targetQuest = quest ?? await this.getQuestById(questId)
        if (!targetQuest) {
          throw new NotFoundError('Quest not found', { questId })
        }
        if (targetQuest.status !== 'active') {
          this.logger.warn('Cannot complete inactive quest', { questId, status: targetQuest.status })
          return false
        }
        const completedAt = completionTimestamp ?? new Date()

        // Mark quest as completed
        await this.firestore
          .collection('quests')
          .doc(questId)
          .update({
            status: 'completed',
            completedAt,
            updatedAt: completedAt
          })

        // Award rewards
        await this.awardQuestRewards(targetQuest.userId, targetQuest.reward)

        // Emit event for other services (gamification, notifications)
        this.events.emit('quest:completed', {
          userId: targetQuest.userId,
          questId: targetQuest.id,
          questType: targetQuest.type,
          reward: targetQuest.reward
        })

        // Invalidate cache
        await this.redisDel(`quest:${questId}`, `quests:active:${targetQuest.userId}`)

        this.logger.info('Quest completed', {
          questId,
          userId: targetQuest.userId,
          reward: targetQuest.reward
        })

        return true
      },
      this.logger
    )
  }

  // ========== QUEST RETRIEVAL ==========

  async getUserQuests(userId: string): Promise<Quest[]> {
    const snapshot = await this.firestore
      .collection('quests')
      .where('userId', '==', userId)
      .where('status', 'in', ['active', 'completed'])
      .orderBy('createdAt', 'desc')
      .limit(50)
      .get()

    return snapshot.docs.map((doc: FirestoreDoc) => this.mapDocToQuest(doc))
  }

  async getActiveQuests(
    userId: string,
    category?: QuestCategory
  ): Promise<Quest[]> {
    // Try cache first
    const cacheKey = `quests:active:${userId}`
    const cached = await this.redisGet(cacheKey)
    if (cached) {
      const parsed = safeJsonParse<Quest[]>(cached)
      if (parsed) {
        const activeFromCache = this.filterActiveQuests(parsed)
        return category ? activeFromCache.filter(q => q.category === category) : activeFromCache
      }
      this.logger.warn('Failed to parse cached quests, cache cleared', { userId })
      await this.redisDel(cacheKey)
    }

    // Fetch from Firestore (avoid composite index requirements by filtering expiry in memory)
    let quests: Quest[] = []
    try {
      const snapshot = await this.firestore
        .collection('quests')
        .where('userId', '==', userId)
        .where('status', '==', 'active')
        .get()
      quests = snapshot.docs.map((doc: FirestoreDoc) => this.mapDocToQuest(doc))
    } catch (error) {
      this.logger.warn('Falling back to broad quest fetch', {
        userId,
        error: (error as Error).message
      })
      const snapshot = await this.firestore
        .collection('quests')
        .where('userId', '==', userId)
        .get()
      quests = snapshot.docs.map((doc: FirestoreDoc) => this.mapDocToQuest(doc))
    }
    const activeQuests = this.filterActiveQuests(quests)

    // Cache for 1 hour
    await this.redisSetex(cacheKey, this.CACHE_TTL, JSON.stringify(activeQuests))

    return category ? activeQuests.filter(q => q.category === category) : activeQuests
  }

  async getQuestById(questId: string): Promise<Quest | null> {
    // Try cache first
    const cacheKey = `quest:${questId}`
    const cached = await this.redisGet(cacheKey)
    if (cached) {
      const parsed = safeJsonParse<Quest>(cached)
      if (parsed) return parsed
    }

    const doc = await this.firestore.collection('quests').doc(questId).get()
    if (!doc.exists) return null

    const quest = this.mapDocToQuest(doc)
    await this.redisSetex(cacheKey, this.CACHE_TTL, JSON.stringify(quest))

    return quest
  }

  // ========== MAINTENANCE ==========

  /**
   * Expire old quests (run daily via cron)
   * 
   * Performance: Uses batch operations for efficient mass updates
   * 
   * @returns Number of expired quests
   */
  async expireOldQuests(): Promise<number> {
    return await trackPerformance(
      'expireOldQuests',
      async () => {
        const snapshot = await this.firestore
          .collection('quests')
          .where('expiresAt', '<', new Date())
          .get()

        if (snapshot.empty) {
          this.logger.debug('No quests to expire')
          return 0
        }

        const activeDocs = snapshot.docs.filter((doc: FirestoreDoc) => {
          const data = doc.data()
          return data?.status === 'active'
        })

        if (!activeDocs.length) {
          this.logger.debug('Found expired quests but none active')
          return 0
        }

        // Use createBatchBuilder for automatic 500 ops limit handling
        const batch = createBatchBuilder(this.firestore)

        for (const doc of activeDocs) {
          batch.updateRef(doc.ref, {
            status: 'expired',
            updatedAt: new Date()
          })
        }

        await batch.commit()

        this.logger.info('Expired old quests', { count: activeDocs.length })
        return activeDocs.length
      },
      this.logger,
      5000 // Warn if takes > 5s
    )
  }

  // ========== PRIVATE HELPERS ==========

  private async createQuest(quest: Quest): Promise<Quest> {
    const ref = this.firestore.collection('quests').doc()
    const fullQuest: Quest = { ...quest, id: ref.id }

    // Remove undefined fields (Quest has optional: completedAt?)
    const sanitizedQuest = removeUndefinedFields(fullQuest)
    await ref.set(sanitizedQuest)
    return fullQuest
  }

  private async createQuestsBatch(quests: Quest[]): Promise<Quest[]> {
    const batch = createBatchBuilder(this.firestore)
    const fullQuests: Quest[] = []

    for (const quest of quests) {
      // Remove undefined fields (Quest has optional: completedAt?)
      const sanitizedQuest = removeUndefinedFields(quest) as unknown as Record<string, unknown>
      const id = batch.addSet('quests', null, sanitizedQuest)
      fullQuests.push({ ...quest, id })
    }

    await batch.commit()
    return fullQuests
  }

  private async awardQuestRewards(userId: string, reward: QuestReward): Promise<void> {
    try {
      // Update user profile with rewards
      const userRef = this.firestore.collection('users').doc(userId)
      
      await this.firestore.runTransaction(async (transaction: FirestoreTransaction) => {
        const userDoc = await transaction.get(userRef)
        if (!userDoc.exists) {
          throw new NotFoundError('User not found', { userId })
        }

        const currentPoints = userDoc.data()?.points ?? 0
        const currentXP = userDoc.data()?.xp ?? 0

        transaction.update(userRef, {
          points: currentPoints + reward.points,
          xp: currentXP + (reward.xp ?? 0),
          updatedAt: new Date()
        })
      })

      // Award season XP if quest has seasonXP reward
      if (reward.seasonXP && reward.seasonXP > 0) {
        this.events.emit('season:xp:add', {
          userId,
          xpAmount: reward.seasonXP,
          reason: 'quest_completion'
        })
      }

      // Emit event for badge unlocks (handled by gamification service)
      if (reward.badge) {
        this.events.emit('badge:unlocked', {
          userId,
          badgeId: reward.badge
        })
      }

      this.logger.info('Quest rewards awarded', { userId, reward })
    } catch (error) {
      this.logger.error('Failed to award quest rewards', { userId, reward, error })
      throw error
    }
  }

  private getExpiryDate(category: QuestCategory): Date {
    const now = new Date()
    switch (category) {
      case 'daily': {
        // Expires at midnight tomorrow
        const tomorrow = new Date(now)
        tomorrow.setDate(tomorrow.getDate() + 1)
        tomorrow.setHours(0, 0, 0, 0)
        return tomorrow
      }
      case 'weekly': {
        // Expires in 7 days
        const nextWeek = new Date(now)
        nextWeek.setDate(nextWeek.getDate() + 7)
        return nextWeek
      }
      default:
        return new Date(now.getTime() + 24 * 60 * 60 * 1000)
    }
  }

  private selectRandomTemplates(templates: QuestTemplate[], count: number): QuestTemplate[] {
    const shuffled = [...templates].sort(() => Math.random() - 0.5)
    return shuffled.slice(0, count)
  }

  private filterActiveQuests(quests: Quest[]): Quest[] {
    const now = Date.now()
    return quests.filter(quest => {
      const expiresAt = toDateSafe(quest.expiresAt)
      if (!expiresAt) {
        this.logger.warn('Quest has invalid expiresAt, skipping', { questId: quest.id })
        return false
      }
      return quest.status === 'active' && expiresAt.getTime() > now
    })
  }

  private mapDocToQuest(doc: FirestoreDocLike): Quest {
    const data = this.requireDocumentData(doc, 'quest')

    return {
      id: doc.id,
      userId: data.userId,
      type: data.type,
      category: data.category,
      title: data.title,
      description: data.description,
      difficulty: data.difficulty,
      targetValue: data.targetValue,
      currentValue: data.currentValue ?? 0,
      progress: data.progress ?? { current: 0, target: data.targetValue ?? 0 },
      status: data.status,
      reward: data.reward ?? { points: 0 },
      expiresAt: toDateOrNow(data.expiresAt),
      createdAt: toDateOrNow(data.createdAt),
      updatedAt: toDateOrNow(data.updatedAt),
      completedAt: toDateOrUndefined(data.completedAt)
    }
  }

  // ========== QUEST TEMPLATES ==========

  private getDailyQuestTemplates(): QuestTemplate[] {
    return [
      // Découverte de lieux (objectif principal)
      { type: 'stops', category: 'daily', title: 'Nouveau spot', description: 'Visite un lieu inédit aujourd’hui', difficulty: 'easy', targetValue: 1, reward: { points: 120, xp: 60 } },
      { type: 'stops', category: 'daily', title: 'Balade trio', description: 'Découvre 3 lieux différents dans la journée', difficulty: 'medium', targetValue: 3, reward: { points: 220, xp: 110 } },
      { type: 'stops', category: 'daily', title: 'Explorateur du jour', description: 'Découvre 5 lieux uniques aujourd’hui', difficulty: 'hard', targetValue: 5, reward: { points: 320, xp: 160, badge: 'explorer' } },

      // Distance solo/total
      { type: 'distance_km', category: 'daily', title: 'Échauffement', description: 'Parcourir 5 km', difficulty: 'easy', targetValue: 5, reward: { points: 120, xp: 60 } },
      { type: 'distance_km', category: 'daily', title: 'Sortie du jour', description: 'Parcourir 20 km', difficulty: 'medium', targetValue: 20, reward: { points: 220, xp: 110 } },
      { type: 'distance_km', category: 'daily', title: 'Long ride', description: 'Parcourir 50 km', difficulty: 'hard', targetValue: 50, reward: { points: 420, xp: 210, badge: 'road_warrior' } },

      // Distance en live (multi)
      { type: 'distance_live_km', category: 'daily', title: 'Ride à deux', description: 'Rouler 10 km en live', difficulty: 'medium', targetValue: 10, reward: { points: 260, xp: 130 } },
      { type: 'distance_live_km', category: 'daily', title: 'Peloton', description: 'Rouler 30 km en live', difficulty: 'hard', targetValue: 30, reward: { points: 520, xp: 260, badge: 'collaborator' } },

      // Distance en clan
      { type: 'distance_clan_km', category: 'daily', title: 'Tour de clan', description: 'Rouler 20 km avec ton clan', difficulty: 'hard', targetValue: 20, reward: { points: 500, xp: 250 } },

      // Partages (limités)
      { type: 'sharing', category: 'daily', title: 'Partager le voyage', description: 'Partage un plan', difficulty: 'easy', targetValue: 1, reward: { points: 110, xp: 55 } },
      { type: 'sharing', category: 'daily', title: 'Plans populaires', description: 'Partage 3 plans', difficulty: 'medium', targetValue: 3, reward: { points: 220, xp: 110 } },
      { type: 'sharing', category: 'daily', title: 'Showcase', description: 'Partage 5 plans', difficulty: 'hard', targetValue: 5, reward: { points: 320, xp: 160 } },

      // Notation de lieux
      { type: 'place_rated', category: 'daily', title: 'Première note', description: 'Noter un lieu', difficulty: 'easy', targetValue: 1, reward: { points: 80, xp: 40 } },
      { type: 'place_rated', category: 'daily', title: 'Critique éclair', description: 'Noter 5 lieux', difficulty: 'medium', targetValue: 5, reward: { points: 200, xp: 100 } },
      { type: 'place_rated', category: 'daily', title: 'Guide local', description: 'Noter 15 lieux', difficulty: 'hard', targetValue: 15, reward: { points: 480, xp: 240, badge: 'socialite' } }
    ]
  }

  private getWeeklyQuestTemplates(): QuestTemplate[] {
    return [
      // Découverte
      { type: 'stops', category: 'weekly', title: 'Tour hebdo', description: 'Découvre 10 lieux uniques cette semaine', difficulty: 'medium', targetValue: 10, reward: { points: 600, xp: 300 } },
      { type: 'stops', category: 'weekly', title: 'Chasseur de spots', description: 'Découvre 20 lieux uniques cette semaine', difficulty: 'hard', targetValue: 20, reward: { points: 1000, xp: 500, badge: 'road_mapper' } },
      { type: 'stops', category: 'weekly', title: 'Collectionneur', description: 'Découvre 35 lieux uniques cette semaine', difficulty: 'hard', targetValue: 35, reward: { points: 1600, xp: 800, badge: 'explorer' } },

      // Distance totale
      { type: 'distance_km', category: 'weekly', title: 'Century', description: 'Rouler 100 km sur la semaine', difficulty: 'medium', targetValue: 100, reward: { points: 800, xp: 400 } },
      { type: 'distance_km', category: 'weekly', title: 'Marathon', description: 'Rouler 250 km sur la semaine', difficulty: 'hard', targetValue: 250, reward: { points: 1500, xp: 750, badge: 'road_warrior' } },
      { type: 'distance_km', category: 'weekly', title: 'Ultra', description: 'Rouler 500 km sur la semaine', difficulty: 'hard', targetValue: 500, reward: { points: 2600, xp: 1300, badge: 'globe_trotter' } },

      // Distance live
      { type: 'distance_live_km', category: 'weekly', title: 'Peloton hebdo', description: 'Rouler 80 km en live', difficulty: 'medium', targetValue: 80, reward: { points: 1100, xp: 550 } },
      { type: 'distance_live_km', category: 'weekly', title: 'Équipe soudée', description: 'Rouler 200 km en live', difficulty: 'hard', targetValue: 200, reward: { points: 1900, xp: 950, badge: 'collaborator' } },

      // Distance clan
      { type: 'distance_clan_km', category: 'weekly', title: 'Sortie de clan', description: 'Rouler 150 km avec ton clan', difficulty: 'hard', targetValue: 150, reward: { points: 2000, xp: 1000 } },

      // Partages
      { type: 'sharing', category: 'weekly', title: 'Ambassadeur', description: 'Partage 5 plans cette semaine', difficulty: 'medium', targetValue: 5, reward: { points: 650, xp: 320, badge: 'weekly_sharer' } },
      { type: 'sharing', category: 'weekly', title: 'Diffusion large', description: 'Partage 10 plans cette semaine', difficulty: 'hard', targetValue: 10, reward: { points: 1100, xp: 550, badge: 'share_pro' } },

      // Notation
      { type: 'place_rated', category: 'weekly', title: 'Critique régulier', description: 'Noter 10 lieux cette semaine', difficulty: 'medium', targetValue: 10, reward: { points: 600, xp: 300 } },
      { type: 'place_rated', category: 'weekly', title: 'Guide expert', description: 'Noter 30 lieux cette semaine', difficulty: 'hard', targetValue: 30, reward: { points: 1400, xp: 700, badge: 'explorer' } }
    ]
  }

  // ========== EVENT LISTENERS ==========

  on(event: string, listener: (...args: unknown[]) => void): void {
    this.events.on(event, listener)
  }

  off(event: string, listener: (...args: unknown[]) => void): void {
    this.events.off(event, listener)
  }

  private requireDocumentData<T extends FirestoreData>(
    doc: FirestoreDocLike<T>,
    entity: string
  ): T {
    const data = doc.data()
    if (!data) {
      throw new Error(`${entity} document ${doc.id} has no data`)
    }
    return data
  }
}
